import { QuestionType } from './enums.js';
/**
 * The subscription plans, as data.
 *
 * One catalog, in `@athena/shared`, because five different places need the
 * same numbers and every one of them would otherwise hold its own copy: the
 * entitlement service deciding whether a tutor may add a sixth student, the
 * GraphQL layer publishing the comparison table, the tutor's subscription
 * screen, the admin screen that assigns a plan, and the tests for all four. A
 * second copy of "Premium allows three students" is not a duplicated constant,
 * it is a second answer to a question the server is supposed to be
 * authoritative about.
 *
 * Here rather than in `apps/api` for the same reason `LIMITS` is here: the
 * browser renders the comparison table and the API enforces it, and a package
 * is what lets both read one definition. Nothing in this file imports Prisma,
 * GraphQL or React, so it is safe in every runtime.
 *
 * **This catalog is the product contract, not client authority for an
 * account.** A client reading `PLANS.PREMIUM.maxStudents` is rendering the
 * contract; the server decides whether a write is allowed by reading the same
 * contract against the tutor's own subscription row. A client that sent a
 * plan code, a price, or a limit would be sending a suggestion, and nothing on
 * the server reads one.
 */
/**
 * The stable identifier of a plan.
 *
 * A code rather than a price or a name: prices change and names are
 * translated, and a subscription row has to go on meaning the same plan
 * through both. These three strings are what the database stores, what GraphQL
 * publishes, and what an admin assigns.
 */
export const PlanCode = {
    FREE: 'FREE',
    PREMIUM: 'PREMIUM',
    PREMIUM_PLUS: 'PREMIUM_PLUS',
};
/** The prepaid period whose total price is being compared. */
export const BillingPeriod = {
    MONTHLY: 'MONTHLY',
    QUARTERLY: 'QUARTERLY',
    ANNUAL: 'ANNUAL',
};
/** Display order and the initial comparison choice. */
export const BILLING_PERIODS = [
    BillingPeriod.MONTHLY,
    BillingPeriod.QUARTERLY,
    BillingPeriod.ANNUAL,
];
export const DEFAULT_BILLING_PERIOD = BillingPeriod.QUARTERLY;
export function isBillingPeriod(value) {
    return BILLING_PERIODS.includes(value);
}
/**
 * The complete amount charged for each available prepaid period.
 *
 * These are totals, never monthly equivalents. Keeping the matrix beside the
 * plan catalog gives a future payment adapter one stable place to associate a
 * provider price id without teaching cards or routes what a plan costs.
 */
const PLAN_PRICES = {
    FREE: {
        MONTHLY: { amountMinorUnits: 0, currency: 'USD' },
        QUARTERLY: { amountMinorUnits: 0, currency: 'USD' },
        ANNUAL: { amountMinorUnits: 0, currency: 'USD' },
    },
    PREMIUM: {
        MONTHLY: { amountMinorUnits: 1500, currency: 'USD' },
        QUARTERLY: { amountMinorUnits: 3900, currency: 'USD' },
        ANNUAL: { amountMinorUnits: 11900, currency: 'USD' },
    },
    PREMIUM_PLUS: {
        MONTHLY: { amountMinorUnits: 2000, currency: 'USD' },
        QUARTERLY: { amountMinorUnits: 5200, currency: 'USD' },
        ANNUAL: { amountMinorUnits: 15900, currency: 'USD' },
    },
};
/**
 * Whether a plan's AI allowance refills.
 *
 * `LIFETIME` is Free's three introductory generations: granted once per tutor
 * account, and never reset — not by a month passing, not by upgrading, and not
 * by coming back to Free afterwards. `MONTHLY` is a paid plan's allowance,
 * which refills on the subscription's own monthly anniversary rather than on
 * the first of the calendar month.
 *
 * The distinction is a property of the plan rather than a branch in the code
 * that consumes it: the usage period a reservation attaches to carries this,
 * so "does this refill" is answered by reading a row instead of by asking
 * which plan somebody was on.
 */
export const AllowanceKind = { LIFETIME: 'LIFETIME', MONTHLY: 'MONTHLY' };
/**
 * The two paid plans share the same V1 capabilities. Premium Plus is "more of
 * it" — more students and monthly AI allowance — rather
 * than a separate feature tier.
 */
const PAID_FEATURES = {
    manualPracticeCreation: true,
    tutorConfigurableAttempts: true,
    availabilityWindow: true,
    timedPractices: true,
    resultsAndExplanations: true,
    completeProgressTracking: true,
    detailedAttemptHistory: true,
    studentSubscriptionRequired: false,
};
export const PLANS = {
    FREE: {
        code: PlanCode.FREE,
        price: PLAN_PRICES.FREE.MONTHLY,
        prices: PLAN_PRICES.FREE,
        maxStudents: 1,
        maxActivePractices: 5,
        maxQuestionsPerPractice: 15,
        generationQuestionTypes: [QuestionType.MULTIPLE_CHOICE],
        /**
         * Three, once, for the life of the account.
         *
         * Not "three a month". A free tier that refills an expensive external call
         * every month is an unbounded bill, and the three exist to show a tutor
         * what generation does rather than to be a way of working.
         */
        ai: { kind: AllowanceKind.LIFETIME, amount: 3, extractions: 9, failures: 3 },
        features: {
            ...PAID_FEATURES,
            tutorConfigurableAttempts: false,
            availabilityWindow: false,
            timedPractices: false,
            completeProgressTracking: false,
            detailedAttemptHistory: false,
        },
    },
    PREMIUM: {
        code: PlanCode.PREMIUM,
        price: PLAN_PRICES.PREMIUM.MONTHLY,
        prices: PLAN_PRICES.PREMIUM,
        maxStudents: 3,
        maxActivePractices: null,
        maxQuestionsPerPractice: 100,
        generationQuestionTypes: Object.values(QuestionType),
        ai: { kind: AllowanceKind.MONTHLY, amount: 35, extractions: 105, failures: 10 },
        features: PAID_FEATURES,
    },
    PREMIUM_PLUS: {
        code: PlanCode.PREMIUM_PLUS,
        price: PLAN_PRICES.PREMIUM_PLUS.MONTHLY,
        prices: PLAN_PRICES.PREMIUM_PLUS,
        maxStudents: 5,
        maxActivePractices: null,
        maxQuestionsPerPractice: 100,
        generationQuestionTypes: Object.values(QuestionType),
        ai: { kind: AllowanceKind.MONTHLY, amount: 60, extractions: 180, failures: 15 },
        features: PAID_FEATURES,
    },
};
/**
 * The plans in the order they are offered, cheapest first.
 *
 * The order is also the ranking: index 0 is the plan every account falls back
 * to, and a higher index is strictly more capacity. `comparePlans` reads this
 * rather than a second table of ranks.
 */
export const PLAN_CODES = [
    PlanCode.FREE,
    PlanCode.PREMIUM,
    PlanCode.PREMIUM_PLUS,
];
/** The plan an account has when it has no paid access. Never null anywhere. */
export const DEFAULT_PLAN_CODE = PlanCode.FREE;
export function isPlanCode(value) {
    return PLAN_CODES.includes(value);
}
export function planOf(code) {
    return PLANS[code];
}
export function priceFor(plan, period) {
    return plan.prices[period];
}
/** True for anything a tutor has to be given rather than gets by existing. */
export function isPaidPlan(code) {
    return PLANS[code].price.amountMinorUnits > 0;
}
/**
 * Negative when `a` is the smaller plan, positive when it is the larger, zero
 * when they are the same. Used to tell an upgrade from a downgrade, which is
 * the difference between "takes effect now" and "takes effect at the end of
 * the period".
 */
export function comparePlans(a, b) {
    return PLAN_CODES.indexOf(a) - PLAN_CODES.indexOf(b);
}
export function isUpgrade(from, to) {
    return comparePlans(from, to) < 0;
}
export function isDowngrade(from, to) {
    return comparePlans(from, to) > 0;
}
/**
 * How long a reservation on an AI generation is honoured before it is treated
 * as abandoned, in minutes.
 *
 * A reservation is taken before the model is called and released when the call
 * fails. A process killed between the two leaves a reservation nothing will
 * ever release, so every reservation carries a lease: past it, the credit is
 * available again and the row is swept the next time the tutor asks for one.
 *
 * Fifteen minutes is well past the longest generation the timeout allows and
 * well short of a tutor noticing a credit missing. There is no sweeper process
 * and deliberately none — the reconciliation happens on the next reservation,
 * which is the only moment the answer matters.
 *
 * The lease is also what limits concurrency (`MAX_CONCURRENT_AI_CALLS`), so it
 * has to outlast the longest call a provider is allowed to make: a lease that
 * ran out mid-call would let a second call start beside it. The API refuses to
 * boot with a provider timeout longer than half of it.
 */
export const AI_RESERVATION_LEASE_MINUTES = 15;
/**
 * How many expensive AI calls — generations and image extractions together —
 * one tutor may have running at once.
 *
 * One. Both front ends make these calls one at a time, so a second while the
 * first is running is either a double click or a script, and neither should
 * double the spend. It is counted from the ledger's live reservations under
 * the tutor's subscription lock, so it holds across tabs, devices and API
 * instances, and a crashed request stops counting when its lease runs out.
 */
export const MAX_CONCURRENT_AI_CALLS = 1;
//# sourceMappingURL=plans.js.map